import { useState, useCallback } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@convex/_generated/api'
import type { Id, Doc } from '@convex/_generated/dataModel'

// Re-export Convex types for convenience
export type Building = Doc<'buildings'>
export type BuildingId = Id<'buildings'>

// Filter parameters for list query
export interface BuildingQueryParams {
  status?: 'draft' | 'published' | 'archived'
  featured?: boolean
}

/**
 * Hook for fetching a list of buildings with optional filters.
 * Uses Convex's real-time queries - data updates automatically when it changes.
 */
export function useBuildings(params: BuildingQueryParams = {}) {
  const data = useQuery(api.buildings.list, {
    status: params.status,
    featured: params.featured,
  })

  // Convex queries auto-refresh, but we provide refetch for API compatibility
  const refetch = useCallback(() => {
    // No-op: Convex queries are real-time and auto-update
  }, [])

  return {
    data,
    isLoading: data === undefined,
    error: null as Error | null, // Convex throws on error
    refetch,
  }
}

/**
 * Hook for fetching a single building by ID.
 * Uses Convex's real-time queries.
 */
export function useBuilding(id: BuildingId | string | undefined) {
  const data = useQuery(
    api.buildings.getById,
    id ? { id: id as BuildingId } : 'skip'
  )

  return {
    data,
    isLoading: data === undefined && id !== undefined,
    error: null as Error | null,
  }
}

/**
 * Hook for fetching a single building by slug.
 * Uses Convex's real-time queries.
 */
export function useBuildingBySlug(slug: string | undefined) {
  const data = useQuery(
    api.buildings.getBySlug,
    slug ? { slug } : 'skip'
  )

  return {
    data,
    isLoading: data === undefined && slug !== undefined,
    error: null as Error | null,
  }
}

/**
 * Hook for fetching building statistics.
 * Returns counts by status (total, published, draft, archived).
 */
export function useBuildingStats() {
  const data = useQuery(api.buildings.stats)

  return {
    data,
    isLoading: data === undefined,
    error: null as Error | null,
  }
}

// Input types for mutations
export interface BuildingCreateInput {
  name: string
  slug: string
  description_short?: string
  description_long?: string
  address?: string
  city?: string
  province?: string
  region?: string
  postal_code?: string
  status: 'draft' | 'published' | 'archived'
  featured: boolean
  sort_order?: number
  badge?: string
}

export type BuildingUpdateInput = Partial<BuildingCreateInput>

/**
 * Hook for creating a new building.
 * Returns a mutation function that can be called with building data.
 */
export function useCreateBuilding() {
  const createMutation = useMutation(api.buildings.create)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async (data: BuildingCreateInput) => {
    setIsPending(true)
    setError(null)
    try {
      const result = await createMutation({
        data,
        ip_address: undefined,
        user_agent: navigator.userAgent,
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [createMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

/**
 * Hook for updating an existing building.
 * Returns a mutation function that can be called with building ID and update data.
 */
export function useUpdateBuilding() {
  const updateMutation = useMutation(api.buildings.update)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async ({ id, data }: { id: BuildingId | string; data: BuildingUpdateInput }) => {
    setIsPending(true)
    setError(null)
    try {
      const result = await updateMutation({
        id: id as BuildingId,
        data,
        ip_address: undefined,
        user_agent: navigator.userAgent,
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [updateMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

/**
 * Hook for deleting a building.
 * Returns a mutation function that can be called with building ID.
 */
export function useDeleteBuilding() {
  const deleteMutation = useMutation(api.buildings.remove)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async (id: BuildingId | string) => {
    setIsPending(true)
    setError(null)
    try {
      const result = await deleteMutation({
        id: id as BuildingId,
        ip_address: undefined,
        user_agent: navigator.userAgent,
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [deleteMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

// Reorder input type
export interface BuildingReorderItem {
  id: BuildingId | string
  sort_order: number
}

/**
 * Hook for reordering buildings.
 * Returns a mutation function that can be called with an array of {id, sort_order} items.
 */
export function useReorderBuildings() {
  const reorderMutation = useMutation(api.buildings.reorder)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async (items: BuildingReorderItem[]) => {
    setIsPending(true)
    setError(null)
    try {
      const result = await reorderMutation({
        items: items.map(item => ({
          id: item.id as BuildingId,
          sort_order: item.sort_order,
        })),
        ip_address: undefined,
        user_agent: navigator.userAgent,
      })
      return result
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [reorderMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}
